/**
 * UserProfileService - API client for user profile management
 * Handles user sync, profile CRUD operations, and activity metrics
 */

import { APIService, APIServiceError } from './APIService';
import {
  UserProfile,
  UserProfileError,
  UserProfileErrorType,
  ActivityMetrics,
  UserPreferences,
  UserMetadata,
} from '../../types/user';

/**
 * API endpoint for user profile operations
 */
const USER_PROFILE_API_URL =
  import.meta.env['VITE_API_USER_PROFILE_URL'] ||
  'https://xyeqvofyij.execute-api.us-west-2.amazonaws.com/prod/users';

/**
 * UserProfileService class for managing user profile operations
 * Extends APIService to inherit retry logic and error handling
 */
export class UserProfileService extends APIService {
  /**
   * Sync user profile with backend after authentication
   * Creates new user record or updates existing one
   * 
   * @param idToken - Cognito ID token (JWT)
   * @returns User profile data
   * @throws UserProfileError if sync fails
   */
  async syncUser(idToken: string): Promise<UserProfile> {
    if (!idToken) {
      throw new UserProfileError(
        UserProfileErrorType.INVALID_TOKEN,
        'ID token is required for user sync',
        400
      );
    }

    try {
      const response = await this.fetchWithRetry(`${USER_PROFILE_API_URL}/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        retries: 3,
        retryDelay: 1000,
        timeout: 15000,
      });

      const data = await response.json();

      // Sync endpoint now returns complete profile, return as-is
      // Ensure all required fields exist with defaults
      const profile: UserProfile = {
        ...data,
        emailVerified: data.emailVerified !== undefined ? data.emailVerified : true,
        authProvider: data.authProvider || 'google',
        preferences: data.preferences || {},
        metadata: data.metadata || {},
        aiGenerateCount: data.aiGenerateCount || 0,
        manualComposeCount: data.manualComposeCount || 0,
        compositionEditCount: data.compositionEditCount || 0,
        compositionShareCount: data.compositionShareCount || 0,
        compositions: data.compositions || [],
        isAdmin: data.isAdmin || false
      };

      return profile;
    } catch (error) {
      throw this.handleUserProfileError(
        error,
        UserProfileErrorType.SYNC_FAILED,
        'Failed to sync user profile'
      );
    }
  }

  /**
   * Get complete user profile
   * 
   * @returns User profile data
   * @throws UserProfileError if not authenticated or request fails
   */
  async getProfile(): Promise<UserProfile> {
    if (!this.isAuthenticated()) {
      throw new UserProfileError(
        UserProfileErrorType.NOT_AUTHENTICATED,
        'User must be authenticated to fetch profile',
        401
      );
    }

    try {
      const response = await this.fetchWithRetry(`${USER_PROFILE_API_URL}/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        retries: 3,
        retryDelay: 1000,
        timeout: 10000,
      });

      const profile: UserProfile = await response.json();

      // Validate response structure
      if (!profile.userId || !profile.email) {
        throw new UserProfileError(
          UserProfileErrorType.VALIDATION_ERROR,
          'Invalid profile data received from server',
          500
        );
      }

      return profile;
    } catch (error) {
      throw this.handleUserProfileError(
        error,
        UserProfileErrorType.USER_NOT_FOUND,
        'Failed to fetch user profile'
      );
    }
  }

  /**
   * Update user profile
   * 
   * @param updates - Partial profile updates (preferences, metadata)
   * @returns Updated user profile
   * @throws UserProfileError if not authenticated or request fails
   */
  async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
    if (!this.isAuthenticated()) {
      throw new UserProfileError(
        UserProfileErrorType.NOT_AUTHENTICATED,
        'User must be authenticated to update profile',
        401
      );
    }

    // Validate updates - only allow preferences and metadata
    const allowedFields = ['preferences', 'metadata'];
    const sanitizedUpdates: { preferences?: UserPreferences; metadata?: UserMetadata } = {};

    for (const key of Object.keys(updates)) {
      if (allowedFields.includes(key)) {
        if (key === 'preferences' && updates.preferences) {
          sanitizedUpdates.preferences = updates.preferences;
        } else if (key === 'metadata' && updates.metadata) {
          sanitizedUpdates.metadata = updates.metadata;
        }
      }
    }

    if (Object.keys(sanitizedUpdates).length === 0) {
      throw new UserProfileError(
        UserProfileErrorType.VALIDATION_ERROR,
        'No valid fields to update. Only preferences and metadata can be updated.',
        400
      );
    }

    try {
      const response = await this.fetchWithRetry(`${USER_PROFILE_API_URL}/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        body: JSON.stringify(sanitizedUpdates),
        retries: 2,
        retryDelay: 1000,
        timeout: 10000,
      });

      const profile: UserProfile = await response.json();

      return profile;
    } catch (error) {
      throw this.handleUserProfileError(
        error,
        UserProfileErrorType.UPDATE_FAILED,
        'Failed to update user profile'
      );
    }
  }

  /**
   * Get user activity metrics
   * 
   * @returns Activity metrics
   * @throws UserProfileError if not authenticated or request fails
   */
  async getActivityMetrics(): Promise<ActivityMetrics> {
    if (!this.isAuthenticated()) {
      throw new UserProfileError(
        UserProfileErrorType.NOT_AUTHENTICATED,
        'User must be authenticated to fetch activity metrics',
        401
      );
    }

    try {
      // Activity metrics are part of the profile, so fetch full profile
      const profile = await this.getProfile();

      // Extract activity metrics from profile
      return {
        aiGenerateCount: profile.aiGenerateCount || 0,
        manualComposeCount: profile.manualComposeCount || 0,
        compositionEditCount: profile.compositionEditCount || 0,
        compositionShareCount: profile.compositionShareCount || 0,
      };
    } catch (error) {
      throw this.handleUserProfileError(
        error,
        UserProfileErrorType.USER_NOT_FOUND,
        'Failed to fetch activity metrics'
      );
    }
  }



  /**
   * Handle and transform errors to UserProfileError
   * 
   * @param error - Original error
   * @param defaultType - Default error type if not APIServiceError
   * @param defaultMessage - Default error message
   * @returns UserProfileError
   */
  private handleUserProfileError(
    error: unknown,
    defaultType: UserProfileErrorType,
    defaultMessage: string
  ): UserProfileError {
    // If already a UserProfileError, return as-is
    if (error instanceof UserProfileError) {
      return error;
    }

    // Handle APIServiceError from base class
    if (error instanceof APIServiceError) {
      let errorType = defaultType;

      // Map status codes to error types
      if (error.status === 401 || error.status === 403) {
        errorType = UserProfileErrorType.INVALID_TOKEN;
      } else if (error.status === 404) {
        errorType = UserProfileErrorType.USER_NOT_FOUND;
      } else if (error.status === 400) {
        errorType = UserProfileErrorType.VALIDATION_ERROR;
      } else if (error.status && error.status >= 500) {
        errorType = UserProfileErrorType.DYNAMODB_ERROR;
      } else if (error.message.toLowerCase().includes('network')) {
        errorType = UserProfileErrorType.NETWORK_ERROR;
      }

      return new UserProfileError(
        errorType,
        error.message || defaultMessage,
        error.status,
        error
      );
    }

    // Handle generic errors
    const err = error as Error;
    return new UserProfileError(
      defaultType,
      err.message || defaultMessage,
      undefined,
      error
    );
  }
}

// Export singleton instance
export const userProfileService = new UserProfileService();
